import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Bell, FileText, AlertCircle, Check, X, UserX } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminNotificationBell — Bell-Icon mit Badge im Admin-Header.
 *
 * Zeigt ungelesene Admin-Notifications (z.B. neue Anamnese-Records,
 * fehlgeschlagene Abos). Polling alle 30s. Klick auf Eintrag:
 *   - anamnese_submitted → /customer-portal/{customer_id}?tab=anamnese
 *   - subscription_payment_failed → /payments
 */
export default function AdminNotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(
        `${API}/admin/notifications?limit=15`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      // silent — Bell ist optional
    }
  }, []);

  useEffect(() => {
    fetchItems();
    const id = setInterval(fetchItems, 30_000);
    return () => clearInterval(id);
  }, [fetchItems]);

  const unreadCount = items.filter((n) => !n.read).length;

  const markRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/notifications/${id}/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
      );
    } catch (_) {
      /* noop */
    }
  };

  const handleClick = async (notification) => {
    await markRead(notification.id);
    setOpen(false);
    if (notification.type === 'anamnese_submitted' && notification.customer_id) {
      navigate(`/customers?customerId=${notification.customer_id}&tab=anamnese`);
    } else if (notification.type === 'subscription_payment_failed') {
      navigate('/payments');
    } else if (notification.type === 'customer_account_deleted') {
      navigate('/admin/deleted-accounts');
    }
  };

  const iconFor = (type) => {
    switch (type) {
      case 'anamnese_submitted':
        return <FileText size={14} className="text-blue-600" />;
      case 'subscription_payment_failed':
        return <AlertCircle size={14} className="text-red-600" />;
      case 'customer_account_deleted':
        return <UserX size={14} className="text-orange-600" />;
      default:
        return <Bell size={14} className="text-gray-500" />;
    }
  };

  const formatDate = (raw) => {
    try {
      const d = new Date(raw);
      const now = new Date();
      const mins = Math.floor((now - d) / 60000);
      if (mins < 1) return 'Gerade eben';
      if (mins < 60) return `${mins} Min.`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours} Std.`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days} Tag${days === 1 ? '' : 'e'}`;
      return d.toLocaleDateString('de-DE');
    } catch (_) {
      return '';
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="admin-notification-bell"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 text-[10px] flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0"
        data-testid="admin-notification-popover"
      >
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold">Benachrichtigungen</h3>
          {unreadCount > 0 && (
            <span className="text-xs text-gray-500">
              {unreadCount} ungelesen
            </span>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-400">
              Keine Benachrichtigungen
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition flex gap-3 ${
                  !n.read ? 'bg-blue-50/40' : ''
                }`}
                data-testid={`notification-item-${n.type}`}
              >
                <div className="flex-shrink-0 mt-0.5">{iconFor(n.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={`text-sm ${
                        !n.read ? 'font-semibold text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      {n.title}
                    </p>
                    {!n.read && (
                      <span className="flex-shrink-0 mt-1 w-2 h-2 rounded-full bg-blue-500" />
                    )}
                  </div>
                  {n.message && (
                    <p className="text-xs text-gray-500 mt-1 leading-snug">
                      {n.message}
                    </p>
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">
                    {formatDate(n.created_at)}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
        {items.some((n) => !n.read) && (
          <div className="border-t px-4 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={async () => {
                const unread = items.filter((n) => !n.read);
                for (const n of unread) {
                  await markRead(n.id);
                }
              }}
              data-testid="notification-mark-all-read"
            >
              <Check size={12} className="mr-1" /> Alle als gelesen markieren
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
